import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { formatDateTime } from "../lib/format";
import type { Transaction } from "../types";

const MIN_EXPLANATION = 10;

const textareaStyle = {
  width: "100%",
  borderRadius: 12,
  border: "1px solid var(--line)",
  padding: "0.65rem 0.75rem",
  font: "inherit",
  resize: "vertical" as const,
};

/**
 * Admin flags a matched receipt as "insufficient information"; the employee
 * then attaches a better receipt or explains the expense.
 */
export function InsufficientInfoPanel({
  txn,
  isAdmin,
  onChanged,
  style,
}: {
  txn: Transaction;
  isAdmin: boolean;
  onChanged: () => Promise<unknown>;
  style?: CSSProperties;
}) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [explainOpen, setExplainOpen] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFlagOpen(false);
    setFlagReason("");
    setExplainOpen(false);
    setExplanation("");
    setError(null);
  }, [txn.id, txn.status]);

  async function post(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.request(`/transactions/${txn.id}/${path}`, {
        method: "POST",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update");
    } finally {
      setBusy(false);
    }
  }

  const flagged = txn.status === "insufficient";
  const explained = txn.status === "matched" && !!txn.insufficient_explanation;
  const canFlag = isAdmin && txn.status === "matched" && !!txn.matched_receipt_id;

  if (!flagged && !explained && !canFlag) return null;

  return (
    <div className="panel stack" style={{ marginBottom: "0.85rem", ...style }}>
      {flagged && (
        <>
          <div className="alert alert-warn" style={{ margin: 0 }}>
            <strong>Insufficient information</strong>
            <div style={{ marginTop: "0.3rem" }}>
              {txn.insufficient_reason
                ? `Admin note: “${txn.insufficient_reason}”`
                : "The attached receipt does not show enough detail for accounting."}
            </div>
            <div style={{ marginTop: "0.3rem", fontSize: "0.85rem" }}>
              Add a better receipt (e.g. an itemised invoice), or explain what this
              expense was for and why there is no more information.
            </div>
          </div>

          {!explainOpen && (
            <>
              <Link className="btn btn-accent btn-block" to={`/capture?txn=${txn.id}`}>
                Add a better receipt
              </Link>
              <button
                type="button"
                className="btn btn-ghost btn-block"
                disabled={busy}
                onClick={() => setExplainOpen(true)}
              >
                Explain instead
              </button>
            </>
          )}

          {explainOpen && (
            <div className="stack" style={{ gap: "0.55rem" }}>
              <div className="field">
                <label htmlFor="insufficient-explanation">
                  What was it for, and why is there no more information?
                </label>
                <textarea
                  id="insufficient-explanation"
                  rows={4}
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder="e.g. Lunch with client ACME to discuss project X. The restaurant only printed a card slip and could not send an invoice."
                  maxLength={2000}
                  style={textareaStyle}
                />
              </div>
              <div className="row" style={{ gap: "0.5rem" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={busy || explanation.trim().length < MIN_EXPLANATION}
                  onClick={() => post("explain", { explanation: explanation.trim() })}
                >
                  {busy ? "Saving…" : "Submit explanation"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => {
                    setExplainOpen(false);
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <button
              type="button"
              className="btn btn-ghost btn-block"
              disabled={busy}
              onClick={() => post("clear-insufficient")}
            >
              Withdraw flag — receipt is fine
            </button>
          )}
        </>
      )}

      {explained && (
        <div className="stack" style={{ gap: "0.35rem" }}>
          <strong style={{ fontSize: "0.95rem" }}>Explanation for missing receipt details</strong>
          {txn.insufficient_reason && (
            <div className="muted" style={{ fontSize: "0.85rem" }}>
              Flagged by admin: “{txn.insufficient_reason}”
            </div>
          )}
          <div style={{ whiteSpace: "pre-wrap" }}>{txn.insufficient_explanation}</div>
          {txn.insufficient_explained_at && (
            <div className="muted" style={{ fontSize: "0.8rem" }}>
              Explained {formatDateTime(txn.insufficient_explained_at)} · included in exports
            </div>
          )}
        </div>
      )}

      {canFlag && !flagOpen && (
        <button
          type="button"
          className="btn btn-ghost btn-block"
          disabled={busy}
          onClick={() => setFlagOpen(true)}
        >
          {explained ? "Flag as insufficient again" : "Flag as insufficient information"}
        </button>
      )}
      {canFlag && flagOpen && (
        <div className="stack" style={{ gap: "0.55rem" }}>
          <div className="field">
            <label htmlFor="insufficient-reason">What is missing? (optional)</label>
            <textarea
              id="insufficient-reason"
              rows={3}
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="e.g. Card slip only — no items, VAT or supplier details."
              maxLength={500}
              style={textareaStyle}
            />
          </div>
          <div className="row" style={{ gap: "0.5rem" }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() => post("flag-insufficient", { reason: flagReason.trim() || null })}
            >
              {busy ? "Saving…" : "Flag — ask employee for more info"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => setFlagOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert">{error}</div>}
    </div>
  );
}
